import {
  createRequestLineStringCoordinates,
  extractLineStringCoordinatesAndRanges,
} from "@/cache/CustomGeoJSONCache/lineStringHelper";
import { aggregateOfListForLine, roundThresholdValue } from "@/cache/CustomGeoJSONCache/utils";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import type { RetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import { customGeoJsonLayerConfig } from "@/constants/layerConfigAttributes";
import type { CustomGeoJsonRequestOption } from "@/layers/geojson/custom";
import { addValueToFeature } from "@/threads/DataProcessingThread/utils/custom-geojson";
import { chunkAndBatchesLineString } from "@/threads/DataProcessingThread/utils/custom-geojson/chunkAndBatchesLineString";
import { enumValues, toEnumValue } from "@/utility/enum";
import { Aggregate } from "@mm/api.meteomatics.com";
import type { Feature, GeoJsonProperties, Geometry, LineString } from "geojson";
import type { DateTime } from "luxon";

export function hydrateLineStringFeatures(
  lineStringFeatures: Array<Feature<LineString, GeoJsonProperties>>,
  parameter: string,
  model: string,
  datetime: DateTime,
  customGeoJsonRequestOption: CustomGeoJsonRequestOption,
  retry: RetryState,
): Promise<Feature<Geometry, GeoJsonProperties>[]>[] {
  const segmentLength =
    customGeoJsonRequestOption.customOption.segmentLength || customGeoJsonLayerConfig.defaultLineSegmentLength;
  const units = customGeoJsonRequestOption.customOption.units || customGeoJsonLayerConfig.defaultLineSegmentUnit;
  const aggregate = toEnumValue<Aggregate>(
    customGeoJsonRequestOption.customOption.aggregate_value || "",
    enumValues(Aggregate),
    Aggregate.max,
  );
  const lineStringBatches = chunkAndBatchesLineString(lineStringFeatures, segmentLength, units);

  const promises: Promise<Feature<Geometry, GeoJsonProperties>[]>[] = [];
  lineStringBatches.forEach((batch, batchIndex) => {
    const correspondingLineFeatures = batch.map((_, index) => {
      return lineStringBatches[batchIndex][index];
    });

    const { coordinates: lineStringCoordinates, indexRanges: lineStringIndexRanges } =
      extractLineStringCoordinatesAndRanges(batch);

    const request = createRequestLineStringCoordinates(lineStringCoordinates, parameter, model, datetime);

    const promise = apiThreadPool
      .GetPointJSON(request, retry, { forcePostRequest: true, preventRequestSplitter: true })
      .then(({ data }) => {
        return correspondingLineFeatures.map((feature, index) => {
          const valuesByLineString = lineStringIndexRanges.map(({ startIndex, endIndex }) => {
            return data[0].coordinates
              .slice(startIndex, endIndex + 1)
              .map((datesValue) => datesValue.dates[0].value as number);
          });
          // valuesByLineString represent also coordinate idx with value. valuesByLineString[0] is the first line. and the values are [3.5, -0.7] start and end point value
          const singleValue = aggregateOfListForLine(aggregate, valuesByLineString[index]);
          if (singleValue != null) {
            // there comes often to long decimal value from aggregation calculation
            return addValueToFeature(feature, roundThresholdValue(singleValue));
          }
          return feature;
        });
      });

    promises.push(promise);
  });

  return promises;
}
